<template>
  <div class="suggested-social-posts-page">
    <!-- Standard page header -->
    <div class="suggested-social-posts-page__header">
      <h1 class="global-h1 standard-page-header__title">
        Social Snapshots
        <BetaBadge class="standard-page-header__beta-badge" />
      </h1>

      <div class="standard-page-header__subtitle-and-link">
        <div class="standard-page-header__subtitle">
          Each week we'll generate new posts to keep your feed fresh.<br />
          You can easily create more posts from your photos at any time!
        </div>

        <!--<LinkWithIcon
          class="standard-page-header__link"
          icon-inset="1px auto auto -3px"
          tag="RouterLink"
          :to="{
            name: 'ShareStopSummaryV2',
            params: { pageId: route.params.pageId },
          }"
        >
          <template #icon><IconEmbedded name="checklist_2" :size="26" /></template>
          <span>Manual Mode</span>
        </LinkWithIcon>-->
      </div>
    </div>
    <!-- / Standard page header -->

    <!-- Atlas Social announcement (feature-flag controlled) -->
    <AtlasSocialAnnouncement v-if="serverFlagEnableAtlasSocial" :customerId="customerId" buttonColor="orange" style="margin-bottom: 100px" />

    <!-- Manual posts section -->
    <div class="manual-posts-section suggested-social-posts-page__manual-posts-section">
      <h1 class="manual-posts-section__title">Posts You Created</h1>

      <AiPostGenerationVisualSchema v-if="!isLoadingAllPosts && manuallyCreatedPosts?.length === 0" class="manual-posts-section__ai-visual-schema" @click:generateButton="openGenFromPhotosModal" />

      <SrpButton v-else class="manual-posts-section__cta-button" @click="openGenFromPhotosModal" :isDisabled="isGeneratingPosts">
        <template #icon><IconEmbedded name="plus_4" :size="20" /></template>
        Generate from Your Photos
      </SrpButton>

      <!-- Posts list -->
      <ul class="posts-list manual-posts-section__posts-list">
        <SocialPostSkeleton v-if="isGeneratingPosts" class="posts-list__post" :isWithShareAndDislikeButtons="true">
          <!-- Gen posts indicator -->
          <div class="generating-posts-indicator">
            <Loader class="generating-posts-indicator__loader-circle" size="small" style="margin: 0" />
            <b>Generating Posts</b>
          </div>
          <!-- / Gen posts indicator -->
        </SocialPostSkeleton>

        <SocialPostPreview
          class="posts-list__post"
          v-for="(socialPost, index) in manuallyCreatedPosts.slice(0, howManyManualPostsToShow)"
          :key="socialPost?.id"
          :assetIds="socialPost.assetIds"
          :caption="socialPost.caption || ''"
          :isWithTextarea="false"
          :isWithShareAndDislikeButtons="true"
          @clickCustomizeAndShare="handleCustomizeAndShare(socialPost)"
          @clickDislike="
            () => {
              postIdToDislike = socialPost?.id;
              isDislikeModalVisible = true;
            }
          "
          @updatePostUrl="
            (newUrl, etag) => {
              socialPost.postUrl = newUrl;
              socialPost._etag = etag;
            }
          "
          :fancyboxKey="socialPost?.id"
          :post="socialPost"
          :showDebug="showDebugLink"
          :debugData="findDebugInfo(socialPost?.id)"
        />
      </ul>
      <!-- / Posts list -->

      <ShowMoreBigButton v-if="howManyManualPostsToShow < manuallyCreatedPosts.length" class="manual-posts-section__show-more-button" @click="howManyPostLinesToShow += 1">
        Show More
        <IconEmbedded name="caret-bottom_3-5" :size="18" color="rgba(120, 120, 120, 1)" style="position: relative; top: 3px; left: 5px" />
      </ShowMoreBigButton>
    </div>
    <!-- / Manual posts section -->

    <SocialPostGenModal
      v-if="isGenFromPhotosModalVisible"
      :customerId="String(route.params.pageId)"
      @close="closeGenFromPhotosModal"
      @generatingStarted="handleGeneratingStarted"
      @generatingCompleted="handleGeneratingCompleted"
    />

    <Loader v-if="isLoadingAllPosts" class="suggested-social-posts-page__loader-icon" />

    <!-- Hold tight placeholder -->
    <div v-else-if="autoGeneratedPosts.length === 0" class="hold-tight-placeholder suggested-social-posts-page__hold-tight-placeholder">
      <div>
        <b>Hold tight!</b><br />
        We'll generate some social posts for you soon.<br />
        Reach out to support@shrpa.com if you have questions in the meantime.
      </div>
    </div>
    <!-- / Hold tight placeholder -->

    <!-- Suggested posts section -->
    <div v-else class="suggested-posts-section suggested-social-posts-page__suggested-posts-section">
      <h2 class="global-h2 suggested-posts-section__title">Weekly Suggestions</h2>

      <template v-for="weeklyRun in autoGeneratedPosts" :key="weeklyRun.weekKey">
        <h3 class="global-h3 suggested-posts-section__subsection-title">
          {{ weeklyRun.heading }}
        </h3>

        <!-- Posts list -->
        <ul class="posts-list suggested-posts-section__posts-list">
          <SocialPostPreview
            class="posts-list__post"
            v-for="socialPost in weeklyRun.weeklyAutoGenerated"
            :key="socialPost.id"
            :assetIds="socialPost.assetIds"
            :caption="socialPost.caption || ''"
            :isWithTextarea="false"
            :isWithShareAndDislikeButtons="true"
            @clickCustomizeAndShare="handleCustomizeAndShare(socialPost)"
            @clickDislike="
              () => {
                postIdToDislike = socialPost.id;
                isDislikeModalVisible = true;
              }
            "
            @updatePostUrl="
              (newUrl, etag) => {
                socialPost.postUrl = newUrl;
                socialPost._etag = etag;
              }
            "
            :fancyboxKey="socialPost.id"
            :post="socialPost"
            :showDebug="showDebugLink"
            :debugData="findDebugInfo(socialPost.id)"
            isWhatPostIsBasedOnVisible
          />
        </ul>
        <!-- / Posts list -->
      </template>
    </div>
    <!-- / Suggested posts section -->

    <SuperUserActionPanel class="suggested-social-posts-page__superuser-action-panel">
      <SrpButton @click="navigateToContentCalendar" color="gray" fill="outlined" size="tiny" style="margin-right: 10px"> New Calendar </SrpButton>
      <SrpButton @click="toggleDebugInfo" color="gray" fill="outlined" size="tiny">
        {{ showDebugLink ? "Hide Debug" : "Show Debug" }}
      </SrpButton>
    </SuperUserActionPanel>

    <DislikePostConfirmModal v-if="isDislikeModalVisible" v-model:isVisible="isDislikeModalVisible">
      <template #footer="{ closeModal, combinedFeedbackString }">
        <SrpButton @click="closeModal" color="gray" fill="outlined">Cancel</SrpButton>

        <SrpButton
          @click="
            async () => {
              await dislikeAndRemovePost(postIdToDislike, combinedFeedbackString);
              postIdToDislike = null;
              closeModal();
            }
          "
        >
          Remove Post
        </SrpButton>
      </template>
    </DislikePostConfirmModal>

    <SocialPostEditAndShareModal
      v-if="isEditAndShareModalVisible"
      v-model:isVisible="isEditAndShareModalVisible"
      :postToEdit="postToEdit"
      :customerId="customerId"
      @close="isEditAndShareModalVisible = false"
      @savePost="() => savePost(postToEdit)"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, inject, Ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";
import lodashCloneDeep from "lodash-es/cloneDeep";
import { MetricSender } from "@helpers/MetricSender";
import { RouteHelper } from "@helpers/RouteHelper";
import AdminContext from "@logic/AdminContext";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SocialPostEditAndShareModal from "@components/SocialPostEditAndShareModal/index.vue";
import SocialPostGenModal from "./SocialPostGenModal/index.vue";
import Loader from "@components/Loader/Loader.vue";
import SocialPostPreview from "@components/SocialPostPreview/index.vue";
import DislikePostConfirmModal from "@components/SocialPostPreview/DislikePostConfirmModal.vue";
import BetaBadge from "@components/BetaBadge.vue";
import SocialPostSkeleton from "@components/SocialPostPreview/SocialPostSkeleton.vue";
import SuperUserActionPanel from "@components/SuperUserActionPanel.vue";
import AiPostGenerationVisualSchema from "@components/AiPostGenerationVisualSchema.vue";
import ShowMoreBigButton from "@components/ui/ShowMoreBigButton.vue";
import FeatureFlagCache from "@logic/FeatureFlagCache";
import AtlasSocialAnnouncement from "@components/AtlasSocialAnnouncement.vue";

// Types
import { SocialMediaPostData, SocialPostWeeklyRun, ImageSelectAIDebugData, SocialPostGenResultPayload } from "@contracts/shareStop";
import { ScreenSize } from "@contracts/screenSize";

// DOM refs
const isGenFromPhotosModalVisible = ref(false);
const isGeneratingPosts = ref(false);

// Router
const route = useRoute();
const router = useRouter();

// Global variables
const screenSize = inject("screenSize") as Ref<ScreenSize>;

// Load posts =================================================================
const autoGeneratedPosts = ref<Array<SocialPostWeeklyRun>>([]);
const manuallyCreatedPosts = ref<Array<SocialMediaPostData>>([]);
const isLoadingAllPosts = ref<boolean>(true);

const customerId = ref<string>(String(route.params.pageId));

// Feature flag
const serverFlagEnableAtlasSocial = ref<boolean>(false);

onMounted(async () => {
  isLoadingAllPosts.value = true;
  await AdminContext.setOrgInContextFromCustomerId(customerId.value);
  try {
    serverFlagEnableAtlasSocial.value = await FeatureFlagCache.GetFeature("social-calendar");
  } catch (e) {
    console.error(e);
    serverFlagEnableAtlasSocial.value = false;
  }
  const postsResult = await ShareStopSummaryRepo.loadAllPostsStructured(customerId.value);

  manuallyCreatedPosts.value = postsResult.manualAICreated;
  autoGeneratedPosts.value = postsResult.weeklyAutoGenerated;
  isLoadingAllPosts.value = false;
});

// Post to edit ===============================================================
const postToEdit = ref<SocialMediaPostData | null>(null);

// Generate social posts =====================================================
function openGenFromPhotosModal() {
  isGenFromPhotosModalVisible.value = true;
}

function closeGenFromPhotosModal() {
  isGenFromPhotosModalVisible.value = false;
}

function handleGeneratingStarted(postCount: number) {
  isGeneratingPosts.value = true;
}

function handleGeneratingCompleted(payload: SocialPostGenResultPayload) {
  isGeneratingPosts.value = false;

  // Extract posts and debug info from the payload
  const newPosts = payload.results.map(result => result.post);
  const newDebugInfo = payload.results.map(result => ({
    customerId: customerId.value,
    sourceEntityType: "post",
    sourceEntityId: result.post.id,
    debugInfo: result.debug,
    imagesInputForDebug: result.imagesInputForDebug,
    // inputPromptForDebug: result.inputPromptForDebug,
  }));

  // Update state
  manuallyCreatedPosts.value = [...newPosts, ...manuallyCreatedPosts.value];
  if (allDebugInfo.value) {
    allDebugInfo.value = [...newDebugInfo, ...allDebugInfo.value];
  }
}

// Debug info =================================================================
const allDebugInfo = ref<Array<ImageSelectAIDebugData> | null>(null);
const showDebugLink = ref<boolean>(false);
async function toggleDebugInfo() {
  showDebugLink.value = !showDebugLink.value;
  if (allDebugInfo.value === null) {
    allDebugInfo.value = await ShareStopSummaryRepo.getDebugInfo(customerId.value);
  }
}

function findDebugInfo(postId: string): ImageSelectAIDebugData | null {
  if (!showDebugLink.value) return null;
  return allDebugInfo.value?.find(debug => debug.sourceEntityId === postId) || null;
}

// Toggle "Edit & Share" modal ================================================
const isEditAndShareModalVisible = ref<boolean>(false);

// Customize and share =================================================
async function handleCustomizeAndShare(socialPost: SocialMediaPostData) {
  postToEdit.value = lodashCloneDeep(socialPost);
  isEditAndShareModalVisible.value = true;
}

// Toggle dislike modal =======================================================
const postIdToDislike = ref<string>("");
const isDislikeModalVisible = ref<boolean>(false);

// Dislike and remove post ====================================================
async function dislikeAndRemovePost(postId: string, reasonText: string) {
  // Check in manually created posts
  const indexInManualPosts = manuallyCreatedPosts.value.findIndex(p => p.id === postId);
  if (indexInManualPosts !== -1) {
    manuallyCreatedPosts.value.splice(indexInManualPosts, 1);
  } else {
    // Check in auto generated posts (weekly runs)
    for (const weeklyRun of autoGeneratedPosts.value) {
      const indexInWeeklyPosts = weeklyRun.weeklyAutoGenerated.findIndex(p => p.id === postId);
      if (indexInWeeklyPosts !== -1) {
        weeklyRun.weeklyAutoGenerated.splice(indexInWeeklyPosts, 1);
        break;
      }
    }
  }

  await ShareStopSummaryRepo.deletePost(customerId.value, postId, reasonText);

  MetricSender.sendMetric("Cust-SocialPost-Delete", { socialPostId: postId });
}

// Save post ==================================================================
function savePost(post: SocialMediaPostData) {
  if (!post) return;

  // Check in manually created posts
  const indexInManualPosts = manuallyCreatedPosts.value.findIndex(p => p.id === post.id);
  if (indexInManualPosts !== -1) {
    manuallyCreatedPosts.value[indexInManualPosts] = post;
    return;
  }

  // Check in auto generated posts (weekly runs)
  for (const weeklyRun of autoGeneratedPosts.value) {
    const indexInWeeklyPosts = weeklyRun.weeklyAutoGenerated.findIndex(p => p.id === post.id);
    if (indexInWeeklyPosts !== -1) {
      weeklyRun.weeklyAutoGenerated[indexInWeeklyPosts] = post;
      return;
    }
  }
}

// How many manual posts to show ==============================================
const howManyPostLinesToShow = ref<number>(1);

const howManyManualPostsToShow = computed<number>(() => {
  let lineSize = 3;
  return lineSize * howManyPostLinesToShow.value - (isGeneratingPosts.value ? 1 : 0);
});

// Open Gen modal on page load ================================================
onMounted(() => {
  if (RouteHelper.getHashParam("isOpenGenModal") === "true") {
    openGenFromPhotosModal();
  }

  location.hash = location.hash.replace("isOpenGenModal=true", "");
});

// Navigation functions =======================================================
function navigateToContentCalendar() {
  router.push({
    name: "CalendarPosts",
    params: { customerId: route.params.pageId },
  });
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Posts list =================================================================
.posts-list {
  gap: $desktop-wide-grid-gap-width;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  list-style: none;

  &__post {
    width: $desktop-wide-right-col-width-1-3rd;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
  .posts-list {
    gap: $desktop-grid-gap-width;

    &__post {
      width: $desktop-right-col-width-1-3rd;
    }
  }
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .posts-list {
    gap: $laptop-grid-gap-width;

    &__post {
      width: $laptop-right-col-width-1-3rd;
    }
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .posts-list {
    gap: $tablet-large-grid-gap-width;

    &__post {
      width: $tablet-large-content-width-1-3rd;
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .posts-list {
    gap: $tablet-grid-gap-width;

    &__post {
      width: $tablet-content-width-1-2nd;
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .posts-list {
    gap: calc($mobile-grid-gap-width * 2);

    &__post {
      width: 100%;
    }
  }
}

// Gen posts indicator ========================================================
.generating-posts-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin: 16px 0;
  color: var(--color-text-secondary);
  font-size: 14px;
  border: 1px solid lightgray;

  padding: 12px;
  border-radius: 12px;
  background: white;

  &__loader-circle {
    width: 50px;
    height: 50px;
    padding: 0;

    :deep(span),
    :deep(span::before),
    :deep(span::after) {
      width: 50px;
      height: 50px;
      margin: 0 !important;
      border-width: 3px;
      left: 0;
    }
    :deep(span::after) {
      border-color: transparent #102f40 transparent transparent !important;
    }
  }
}

// Manual posts section =======================================================
.manual-posts-section {
  &__title {
    padding-bottom: 13px;
    margin-bottom: 27px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__ai-visual-schema {
  }

  &__cta-button {
    margin-bottom: 20px;
  }

  &__posts-list {
    margin-bottom: 35px;
  }

  &__show-more-button {
  }
}

// Suggested posts section ====================================================
.suggested-posts-section {
  &__title {
    padding-bottom: 13px;
    margin-bottom: 27px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__subsection-title {
    margin-bottom: 8px;
    font-weight: 600;
  }

  &__posts-list {
    margin-bottom: 30px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// Suggested social posts =====================================================
.suggested-social-posts-page {
  width: $desktop-wide-right-col-width;
  margin: 0 auto;
  display: flex;
  flex-direction: column;

  &__header {
    margin-bottom: 50px;
  }

  &__loader-icon {
  }

  &__hold-right-placeholder {
  }

  &__manual-posts-section {
    margin-bottom: 60px;
  }

  &__suggested-posts-section {
    margin-bottom: 50px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__superuser-action-panel {
    align-self: flex-end;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
  .suggested-social-posts-page {
    width: $desktop-right-col-width;
  }
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .suggested-social-posts-page {
    width: $laptop-right-col-width;
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .suggested-social-posts-page {
    width: $tablet-large-content-width;
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .suggested-social-posts-page {
    width: $tablet-content-width;
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .suggested-social-posts-page {
    width: $mobile-content-width;
  }
}
</style>
